import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Alert, Snackbar } from '@mui/material';
import { AccountMovement, DebtSearchParams } from '../types';
import { apiClient } from '../services/api';
import { DebtList } from '../features/debts';
import { DebtListRef } from '../features/debts';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import InstallmentDueList from '../components/debt/InstallmentDueList';
import CompanyScopeBanner from '../components/common/CompanyScopeBanner';

const installmentFilters = ['overdue', 'today', 'soon', 'week', 'month'] as const;

const defaultMovementRange = () => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 365);
  const format = (value: Date) => {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  return { startDate: format(start), endDate: format(end) };
};
const fetchDebtsPage = (params: DebtSearchParams) => apiClient.getAccountMovements(params);

const Debts: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const debtListRef = useRef<DebtListRef>(null);
  const [searchParams, setSearchParams] = useState<DebtSearchParams>({
    page: 0,
    size: 10,
    sort: 'lastActivityDateTime',
    direction: 'desc',
    ...defaultMovementRange(),
    companyId: new URLSearchParams(location.search).get('companyId') || undefined,
  });

  useEffect(() => {
    const next = new URLSearchParams();
    const numericKeys: Array<keyof DebtSearchParams> = ['page', 'size', 'minAmount', 'maxAmount'];
    (Object.keys(searchParams) as Array<keyof DebtSearchParams>).forEach(key => {
      const value = searchParams[key];
      if (value !== undefined && value !== '') next.set(String(key), String(value));
    });
    numericKeys.forEach(key => {
      const value = searchParams[key];
      if (value !== undefined) next.set(String(key), String(value));
    });
    const current = new URLSearchParams(location.search);
    const installmentFilter = current.get('installmentFilter');
    if (installmentFilter) next.set('installmentFilter', installmentFilter);
    const nextSearch = next.toString();
    if (nextSearch !== location.search.replace(/^\?/, '')) {
      navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true });
    }
  }, [location.pathname, location.search, navigate, searchParams]);

  // Notification states
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleCustomerClick = useCallback((customerId: string, customerName: string) => {
    // Navigate to customer detail page
    navigate(`/customers/${customerId}`, { 
      state: { customerName, returnTo: '/debts', companyId: searchParams.companyId }
    });
  }, [navigate, searchParams.companyId]);

  const showNotification = useCallback((message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setNotification({ open: true, message, severity });
  }, []);

  const hideNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  const debtsQuery = usePaginatedQuery<AccountMovement, DebtSearchParams>(
    searchParams,
    fetchDebtsPage,
    'Failed to load debts',
    'search',
    true,
    'debts',
  );

  const requestedInstallmentFilter = new URLSearchParams(location.search).get('installmentFilter');
  const initialInstallmentFilter = installmentFilters.includes(requestedInstallmentFilter as typeof installmentFilters[number])
    ? requestedInstallmentFilter as typeof installmentFilters[number]
    : undefined;

  useEffect(() => {
    if (debtsQuery.error) {
      showNotification(debtsQuery.error, 'error');
    }
  }, [debtsQuery.error, showNotification]);

  // Handle pagination - unified interface for TanStack React Table  
  const handlePaginationModelChange = (model: { page: number; pageSize: number }) => {
    setSearchParams(prev => ({
      ...prev,
      page: prev.size !== model.pageSize ? 0 : model.page,
      size: model.pageSize,
    }));
  };

  const handleSortChange = (field?: string, direction?: 'asc' | 'desc') => {
    setSearchParams(prev => ({ ...prev, sort: field, direction, page: 0 }));
  };

  const handleFilterChange = (field: keyof DebtSearchParams, value: DebtSearchParams[keyof DebtSearchParams]) => {
    setSearchParams(prev => ({ ...prev, [field]: value, page: 0 })); // Reset to first page when filtering
  };

  const handleFiltersChange = (filters: Partial<DebtSearchParams>) => {
    const clearingAllDates = filters.startDate === undefined && filters.endDate === undefined;
    setSearchParams(prev => ({
      ...prev,
      ...filters,
      ...(clearingAllDates ? defaultMovementRange() : {}),
      page: 0,
    }));
  };

  useEffect(() => {
    // Check URL parameters to trigger actions
    const urlSearchParams = new URLSearchParams(location.search);
    const action = urlSearchParams.get('action');
    
    if (action === 'payment' && debtListRef.current) {
      // Trigger payment dialog
      debtListRef.current.handleAddPayment?.();
      // Clear the URL parameter
      const next = new URLSearchParams(location.search);
      next.delete('action');
      navigate({ pathname: '/debts', search: next.toString() ? `?${next}` : '' }, { replace: true });
    } else if (action === 'add' && debtListRef.current) {
      // Trigger add debt dialog
      debtListRef.current.handleAddDebt?.();
      // Clear the URL parameter
      const next = new URLSearchParams(location.search);
      next.delete('action');
      navigate({ pathname: '/debts', search: next.toString() ? `?${next}` : '' }, { replace: true });
    }
  }, [location.search, navigate]);

  return (
    <Container maxWidth={false} disableGutters sx={{ pt: 0, pb: { xs: 2, md: 3 } }}>
      <CompanyScopeBanner companyId={searchParams.companyId} />
      <DebtList 
        ref={debtListRef}
        paginatedData={debtsQuery.data}
        loading={debtsQuery.isLoading}
        searchParams={searchParams}
        onPaginationModelChange={handlePaginationModelChange}
        onSortChange={handleSortChange}
        onFilterChange={handleFilterChange}
        onFiltersChange={handleFiltersChange}
        onCustomerClick={handleCustomerClick} 
      />

      <InstallmentDueList onCustomerClick={handleCustomerClick} initialFilter={initialInstallmentFilter} />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={hideNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={hideNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Debts;
